import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { ArticleOutlined, CloudUploadOutlined, OpenInNew } from '@mui/icons-material';
import DocumentsService, { InfoAboutDocuments } from '../../logic/DocumentsService';

type DocumentField = Exclude<keyof InfoAboutDocuments, 'id' | 'lastUpdated'>;

const documentDefinitions: Array<{ field: DocumentField; label: string; description: string }> = [
    { field: 'privacyPolicyUrl', label: 'Политика конфиденциальности', description: 'Правила хранения и защиты персональных данных.' },
    { field: 'termsOfServiceUrl', label: 'Условия обслуживания', description: 'Условия аренды, оплаты, доставки и возврата.' },
    { field: 'userAgreementUrl', label: 'Пользовательское соглашение', description: 'Основной договор между сервисом и пользователем.' },
    { field: 'dataProcessingPolicyUrl', label: 'Политика обработки данных', description: 'Согласия и порядок обработки персональных данных.' },
];

const DocumentsComponent: React.FC = () => {
    const [documents, setDocuments] = useState<InfoAboutDocuments | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploadingField, setUploadingField] = useState<DocumentField | null>(null);

    const fetchDocuments = useCallback(async () => {
        try {
            setError(null);
            setDocuments(await DocumentsService.getAllDocuments());
        } catch {
            setError('Не удалось загрузить документы. Проверьте backend и повторите попытку.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchDocuments();
    }, [fetchDocuments]);

    const missingCount = useMemo(() => documents
        ? documentDefinitions.filter(({ field }) => !documents[field]).length
        : 0, [documents]);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, field: DocumentField) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        try {
            setError(null);
            setUploadingField(field);
            setDocuments(await DocumentsService.updateDocument(field, file));
        } catch {
            setError('Не удалось обновить документ. Допустим только корректный PDF-файл.');
        } finally {
            setUploadingField(null);
        }
    };

    return (
        <Container maxWidth="lg" disableGutters>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {missingCount > 0 && (
                        <Alert severity="warning">
                            Не загружено документов: {missingCount}. Они не будут доступны клиентам до публикации.
                        </Alert>
                    )}

                    {documentDefinitions.map(({ field, label, description }) => {
                        const url = documents?.[field];
                        const inputId = `document-upload-${field}`;
                        const isUploading = uploadingField === field;

                        return (
                            <Paper
                                key={field}
                                variant="outlined"
                                sx={{
                                    p: { xs: 2, md: 2.5 },
                                    borderRadius: 3,
                                    borderColor: 'rgba(15, 42, 86, 0.12)',
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    flexDirection: { xs: 'column', md: 'row' },
                                }}
                            >
                                <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: 'rgba(15, 42, 86, 0.08)', color: 'primary.main', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
                                    <ArticleOutlined />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" fontWeight={900}>{label}</Typography>
                                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                                    <Typography variant="caption" color={url ? 'success.main' : 'warning.main'} fontWeight={800}>
                                        {url ? 'Опубликован' : 'Не загружен'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                                    {url && (
                                        <Button href={url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNew />} variant="outlined">
                                            Открыть
                                        </Button>
                                    )}
                                    <input hidden accept="application/pdf,.pdf" id={inputId} type="file" onChange={(event) => handleFileChange(event, field)} />
                                    <Button
                                        component="label"
                                        htmlFor={inputId}
                                        variant="contained"
                                        startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlined />}
                                        disabled={Boolean(uploadingField)}
                                    >
                                        {url ? 'Заменить' : 'Загрузить'}
                                    </Button>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Container>
    );
};

export default DocumentsComponent;
